import { Badge, Group, Paper, Stack, Text } from '@mantine/core';

// Renders a stack of pill rows, one per filter defined in question.filters.
// Value shape:
//   Single-select filter:  answers.narrow[filter.key] = 'vp'
//   Multi-select filter:   answers.narrow[filter.key] = ['vp', 'director']
// 'any' is exclusive on both — selecting 'any' clears others; selecting anything else clears 'any'.
export default function NarrowFiltersScreen({ question, value, onChange, hideHeader = false }) {
  if (!question) return null;
  const filters = Array.isArray(question.filters) ? question.filters : [];
  const current = value && typeof value === 'object' ? value : {};

  function isSelected(filter, optionValue) {
    const stored = current[filter.key];
    if (filter.multi) {
      if (Array.isArray(stored) && stored.length) return stored.includes(optionValue);
      // Default to 'any' if nothing stored yet.
      return optionValue === 'any';
    }
    return (stored || 'any') === optionValue;
  }

  function togglePill(filter, optionValue) {
    if (!filter.multi) {
      // Single-select: replace.
      onChange({ ...current, [filter.key]: optionValue });
      return;
    }
    // Multi-select with exclusive 'any'.
    const stored = Array.isArray(current[filter.key]) ? current[filter.key] : [];
    if (optionValue === 'any') {
      onChange({ ...current, [filter.key]: ['any'] });
      return;
    }
    const withoutAny = stored.filter((v) => v !== 'any');
    const next = withoutAny.includes(optionValue)
      ? withoutAny.filter((v) => v !== optionValue)
      : [...withoutAny, optionValue];
    // If nothing left selected, fall back to 'any' to keep the row visually anchored.
    onChange({ ...current, [filter.key]: next.length ? next : ['any'] });
  }

  return (
    <Stack gap="lg">
      {!hideHeader && (
        <Stack gap={6}>
          <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 24, lineHeight: 1.25 }}>
            {question.ask}
          </Text>
          {question.sub && (
            <Text size="sm" style={{ color: 'var(--aviary-muted)', lineHeight: 1.5 }}>
              {question.sub}
            </Text>
          )}
        </Stack>
      )}

      <Stack gap="md">
        {filters.map((filter) => {
          const options = Array.isArray(filter.options) ? filter.options : [];
          return (
            <Paper key={filter.key} p="md" radius="md" withBorder style={{ background: 'var(--aviary-surface)' }}>
              <Stack gap="xs">
                <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.3 }}>
                  {(filter.label || filter.key).toUpperCase()}
                  {filter.multi && (
                    <Text component="span" size="xs" ml={6} style={{ color: 'var(--aviary-muted)', opacity: 0.7, letterSpacing: 0 }}>
                      · pick as many
                    </Text>
                  )}
                </Text>
                <Group gap="xs" wrap="wrap">
                  {options.map((opt) => {
                    const selected = isSelected(filter, opt.value);
                    return (
                      <Badge
                        key={opt.value}
                        component="button"
                        type="button"
                        onClick={() => togglePill(filter, opt.value)}
                        color="sage"
                        variant={selected ? 'filled' : 'light'}
                        size="lg"
                        radius="sm"
                        style={{
                          cursor: 'pointer',
                          textTransform: 'none',
                          fontWeight: selected ? 700 : 500,
                          border: 'none',
                          padding: '10px 14px',
                        }}
                      >
                        {opt.label}
                      </Badge>
                    );
                  })}
                </Group>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    </Stack>
  );
}
